"use client";

import React, { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import {
  Kanban,
  Table as TableIcon,
  Download,
  RefreshCw,
  LogOut,
  Search,
  DollarSign,
  TrendingUp,
  Users,
  Briefcase,
  CheckCircle,
  Eye,
  Building2,
  FileText,
  MessageSquare,
  CreditCard,
  Bot,
  BarChart3,
  UserPlus,
  Sparkles,
  Globe,
  Phone,
  Plus,
  ShieldCheck,
  UploadCloud,
  Sliders,
  ArrowDownToLine,
  ArrowUpFromLine,
  Clock,
} from "lucide-react";

import { Lead } from "@/lib/db";
import { Button } from "@/components/ui/Button";
import KanbanBoard from "./KanbanBoard";
import LeadDetailModal from "./LeadDetailModal";
import StageApprovalModal from "./StageApprovalModal";
import TeamManagementTab from "./TeamManagementTab";
import ClientAgreementGenerator from "./ClientAgreementGenerator";
import { WhatsAppChatDrawer } from "./WhatsAppChatDrawer";
import PaymentManagementModal from "./PaymentManagementModal";
import AiSalesAgentTab from "./AiSalesAgentTab";
import ReportManagementTab from "./ReportManagementTab";
import ComplianceTab from "./ComplianceTab";
import AddLeadModal from "./AddLeadModal";
import ImportLeadsModal from "./ImportLeadsModal";
import WorkspaceSettingsModal from "./WorkspaceSettingsModal";
import QuotaUsageBadge from "./QuotaUsageBadge";
import UpgradePlanModal from "./UpgradePlanModal";
import CrmPwaInstallButton from "./CrmPwaInstallButton";
import TenantTrialsTab from "./TenantTrialsTab";
import { UserSubscriptionSummary } from "@/lib/quotas";
import { formatCurrencyValue, getCurrencyBadge } from "@/lib/currency";
import { WorkspaceConfig, getDefaultWorkspaceConfig } from "@/lib/workspacePresets";
import { downloadCsvFile } from "@/lib/csvDownload";

interface StatsData {
  totalLeads: number;
  activeOpportunities: number;
  pipelineValue: number;
  pipelineValueUSD?: number;
  pipelineValueINR?: number;
  pipelineValueAED?: number;
  wonRevenue: number;
  wonRevenueUSD?: number;
  wonRevenueINR?: number;
  wonRevenueAED?: number;
  wonDealsCount: number;
  conversionRate: number;
}

interface CrmDashboardProps {
  currentUser: {
    id: number;
    username: string;
    role: string;
    fullName: string;
    planTier?: string;
    industry?: string;
    companyName?: string;
    phone?: string;
  };
  subscription?: UserSubscriptionSummary | null;
  onSubscriptionUpdate?: () => void;
  onLogout: () => void;
}

export default function CrmDashboard({
  currentUser,
  subscription,
  onSubscriptionUpdate,
  onLogout,
}: CrmDashboardProps) {
  const [viewMode, setViewMode] = useState<"kanban" | "table" | "team" | "agreements" | "ai-sales" | "trials" | "reports" | "compliance">("kanban");
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [pendingOutreachCount, setPendingOutreachCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [chatLead, setChatLead] = useState<Lead | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [priorityFilter, setPriorityFilter] = useState("ALL");
  const [serviceFilter, setServiceFilter] = useState("ALL");
  const [leadModeFilter, setLeadModeFilter] = useState<"REAL" | "TEST" | "ALL">("ALL");
  const [dateScopeFilter, setDateScopeFilter] = useState<"ALL" | "TODAY" | "7DAYS" | "30DAYS" | "THIS_MONTH">("ALL");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isSeedingLeads, setIsSeedingLeads] = useState(false);

  // Multi-Tenant Isolation & Workspace Selector (Super Admin)
  const isPlatformAdmin = currentUser.id === 1 || currentUser.username === "admin";
  const [workspaceScope, setWorkspaceScope] = useState<string>("all");
  const [tenants, setTenants] = useState<{ id: number; username: string; fullName: string; companyName: string | null; leadCount: number }[]>([]);

  // Configurable Workspace Profile, Dynamic Fields, and Pipeline Stages
  const [workspaceConfig, setWorkspaceConfig] = useState<WorkspaceConfig>(() =>
    getDefaultWorkspaceConfig(currentUser.id, currentUser.companyName, (currentUser as any).industry)
  );
  const [isWorkspaceSettingsOpen, setIsWorkspaceSettingsOpen] = useState(false);

  const [repFilter, setRepFilter] = useState("ALL");
  const [teamMembers, setTeamMembers] = useState<Array<{ id: number; username: string; full_name: string; role: string; is_active?: number }>>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchData = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleSeedSampleLeads = async () => {
    setIsSeedingLeads(true);
    try {
      const res = await fetch("/api/crm/leads/seed", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchData();
      }
    } catch (e) {
      console.warn("Failed to seed sample leads:", e);
    } finally {
      setIsSeedingLeads(false);
    }
  };

  // Fetch Team Members for current company / workspace
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const teamUrl = isPlatformAdmin && workspaceScope !== "mine" && workspaceScope !== "all"
          ? `/api/crm/team?tenant_id=${workspaceScope}`
          : "/api/crm/team";
        const res = await fetch(teamUrl);
        const data = await res.json();
        if (data.success && data.team) {
          setTeamMembers(data.team);
        }
      } catch (e) {}
    };
    fetchTeam();
  }, [workspaceScope, isPlatformAdmin, refreshTrigger]);

  // Load Workspace Configuration
  useEffect(() => {
    const targetUid = isPlatformAdmin && workspaceScope !== "all" && workspaceScope !== "mine" 
      ? Number(workspaceScope) 
      : currentUser.id;
    fetch(`/api/crm/workspace/config?userId=${targetUid}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.config) {
          setWorkspaceConfig(d.config);
        }
      })
      .catch((err) => console.error("Error loading workspace config:", err));
  }, [currentUser.id, isPlatformAdmin, workspaceScope, refreshTrigger]);

  const getFieldLabel = (key: string, fallback: string) => {
    const f = workspaceConfig.fields.find((field) => field.key === key);
    return f?.label || fallback;
  };

  const isFieldVisible = (key: string) => {
    const f = workspaceConfig.fields.find((field) => field.key === key);
    return f ? f.visible !== false : true;
  };

  // Super Admin: Load active client tenants for workspace switching
  useEffect(() => {
    if (isPlatformAdmin) {
      fetch("/api/crm/tenants")
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d && d.tenants) setTenants(d.tenants);
        })
        .catch(() => {});
    }
  }, [isPlatformAdmin, refreshTrigger]);

  useEffect(() => {
    let ignore = false;

    async function load() {
      try {
        const statsParams = new URLSearchParams();
        if (isPlatformAdmin) {
          if (workspaceScope === "all") statsParams.set("scope", "all");
          else if (workspaceScope !== "mine") statsParams.set("tenant_id", workspaceScope);
          else statsParams.set("scope", "mine");
        }
        const statsRes = await fetch(`/api/crm/stats?${statsParams.toString()}`);
        if (statsRes.ok && !ignore) {
          const statsJson = await statsRes.json();
          setStats(statsJson.stats);
        }

        if (isPlatformAdmin) {
          try {
            const outreachRes = await fetch("/api/crm/outreach/queue?limit=1");
            if (outreachRes.ok && !ignore) {
              const oJson = await outreachRes.json();
              if (oJson.stats) setPendingOutreachCount(oJson.stats.pendingCount || 0);
            }
          } catch (e) { }
        }

        const params = new URLSearchParams();
        if (searchTerm) params.set("search", searchTerm);
        if (statusFilter !== "ALL") params.set("status", statusFilter);
        if (priorityFilter !== "ALL") params.set("priority", priorityFilter);
        if (serviceFilter !== "ALL") params.set("service_type", serviceFilter);
        if (leadModeFilter !== "ALL") params.set("lead_mode", leadModeFilter);

        // Tenant Scoping
        if (isPlatformAdmin) {
          if (workspaceScope === "all") params.set("scope", "all");
          else if (workspaceScope !== "mine") params.set("tenant_id", workspaceScope);
          else params.set("scope", "mine");
        }

        // Date Scope Filter
        if (dateScopeFilter === "TODAY") {
          const today = new Date().toISOString().split("T")[0];
          params.set("startDate", today);
        } else if (dateScopeFilter === "7DAYS") {
          const d = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];
          params.set("startDate", d);
        } else if (dateScopeFilter === "30DAYS") {
          const d = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];
          params.set("startDate", d);
        } else if (dateScopeFilter === "THIS_MONTH") {
          const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0];
          params.set("startDate", firstDay);
        }

        params.set("limit", "2000");
        if (repFilter !== "ALL") params.set("assigned_rep", repFilter);
        const leadsRes = await fetch(`/api/crm/leads?${params.toString()}`);
        if (leadsRes.ok && !ignore) {
          const leadsJson = await leadsRes.json();
          setLeads(leadsJson.leads || []);
        }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [searchTerm, statusFilter, priorityFilter, serviceFilter, leadModeFilter, dateScopeFilter, workspaceScope, repFilter, refreshTrigger]);


  const [automationNotification, setAutomationNotification] = useState<{
    stage: string;
    message: string;
  } | null>(null);

  const launchVictoryConfetti = () => {
    if (typeof window === "undefined") return;
    const canvas = document.createElement("canvas");
    canvas.style.position = "fixed";
    canvas.style.top = "0";
    canvas.style.left = "0";
    canvas.style.width = "100vw";
    canvas.style.height = "100vh";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "99999";
    document.body.appendChild(canvas);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: Array<{
      x: number;
      y: number;
      size: number;
      color: string;
      speedX: number;
      speedY: number;
      rotation: number;
      rotSpeed: number;
    }> = [];

    const colors = ["#2563eb", "#38bdf8", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6"];
    for (let i = 0; i < 120; i++) {
      particles.push({
        x: canvas.width / 2 + (Math.random() - 0.5) * 400,
        y: canvas.height / 3 + (Math.random() - 0.5) * 200,
        size: Math.random() * 8 + 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        speedX: (Math.random() - 0.5) * 16,
        speedY: Math.random() * -12 - 6,
        rotation: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 10,
      });
    }

    let frame = 0;
    const animate = () => {
      frame++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.speedX;
        p.y += p.speedY;
        p.speedY += 0.4;
        p.rotation += p.rotSpeed;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        ctx.restore();
      });

      if (frame < 180) {
        requestAnimationFrame(animate);
      } else {
        if (canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      }
    };
    animate();
  };

  const [stageApprovalData, setStageApprovalData] = useState<{
    lead: Lead;
    targetStage: "Proposal Sent" | "Negotiation" | "Closed Won";
  } | null>(null);

  const executeStatusUpdate = async (
    leadId: number,
    newStatus: Lead["status"],
    dealValue?: number,
    currency?: string,
    options?: any
  ) => {
    try {
      const payload: any = { status: newStatus };
      if (dealValue !== undefined) payload.estimated_deal_value = dealValue;
      if (currency !== undefined) payload.currency = currency;
      if (options) payload.options = options;


      const res = await fetch(`/api/crm/leads/${leadId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.automationMessage) {
          setAutomationNotification({ stage: newStatus, message: data.automationMessage });
          setTimeout(() => setAutomationNotification(null), 6000);
        }
        if (newStatus === "Closed Won") {
          launchVictoryConfetti();
        }

        // Optimistically update local state
        setLeads((prev) =>
          prev.map((l) => (l.id === leadId ? (data.lead || { ...l, status: newStatus }) : l))
        );
        fetchData();
      }
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const handleStatusChange = (leadId: number, newStatus: Lead["status"]) => {
    const targetLead = leads.find((l) => l.id === leadId);
    if (!targetLead) return;

    if (newStatus === "Proposal Sent" || newStatus === "Negotiation" || newStatus === "Closed Won") {
      setStageApprovalData({ lead: targetLead, targetStage: newStatus });
    } else {
      executeStatusUpdate(leadId, newStatus);
    }
  };


  const handlePriorityChange = async (leadId: number, newPriority: Lead["priority"]) => {
    try {
      const res = await fetch(`/api/crm/leads/${leadId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priority: newPriority }),
      });

      if (res.ok) {
        setLeads((prev) =>
          prev.map((l) => (l.id === leadId ? { ...l, priority: newPriority } : l))
        );
      }
    } catch (err) {
      console.error("Failed to update priority:", err);
    }
  };

  const exportLeadsDirectlyFromMemory = (list: Lead[]) => {
    if (!list || list.length === 0) {
      alert("No leads found in pipeline to export.");
      return;
    }

    const companySlug = (currentUser.companyName || currentUser.username || "workspace")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_")
      .slice(0, 30);
    const filename = `${companySlug}_leads_${new Date().toISOString().slice(0, 10)}.csv`;

    const visibleFields = workspaceConfig?.fields?.filter((f) => f.visible !== false) || [];
    const headers = [
      "ID",
      ...visibleFields.map((f) => f.label),
      "Pipeline Stage",
      "Currency",
      "Created At",
    ];

    const escapeVal = (val: any) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const rows = list.map((l) => {
      const fieldValues = visibleFields.map((f) => {
        const key = f.key;
        if (key === "name") return escapeVal(l.name);
        if (key === "company_name") return escapeVal(l.company_name || "");
        if (key === "phone") return escapeVal(l.phone || "");
        if (key === "email") return escapeVal(l.email || "");
        if (key === "service_type") return escapeVal(l.service_type || "");
        if (key === "quantity") return l.quantity !== null && l.quantity !== undefined ? l.quantity : "";
        if (key === "unit") return escapeVal(l.unit || "");
        if (key === "estimated_deal_value") return l.estimated_deal_value || 0;
        if (key === "country_or_city") return escapeVal(l.country_or_city || "");
        if (key === "priority") return escapeVal(l.priority || "WARM");
        if (key === "notes") return escapeVal(l.notes || "");
        if (l.custom_fields && (l.custom_fields as any)[key]) {
          return escapeVal((l.custom_fields as any)[key]);
        }
        return '""';
      });

      return [
        l.id,
        ...fieldValues,
        escapeVal(l.status),
        escapeVal(l.currency || "INR"),
        escapeVal(l.created_at || ""),
      ];
    });

    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map((r) => r.join(","))].join("\r\n");
    const targetUid = isPlatformAdmin && workspaceScope !== "all" && workspaceScope !== "mine"
      ? Number(workspaceScope)
      : currentUser.id;
    downloadCsvFile(filename, csvContent, `/api/crm/export?user_id=${targetUid}`);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      exportLeadsDirectlyFromMemory(leads);
    } catch (err) {
      console.error("Export error:", err);
    } finally {
      setExporting(false);
    }
  };


  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col">
      {/* Top CRM App Bar */}
      <header className="sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 px-4 sm:px-8 py-3 print:hidden space-y-3">
        {/* Row 1: Brand & User Identity (Left) + Quota, Refresh, Website & Sign Out (Right) */}
        <div className="max-w-[1700px] mx-auto flex items-center justify-between gap-2 sm:gap-4 flex-wrap sm:flex-nowrap">
          {/* Brand & User Info */}
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl overflow-hidden shadow-md border border-cyan-500/30 shrink-0 bg-[#0B132B]">
              <img src="/brand/app-icon.svg?v=3" alt="Johnson Softwares" className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <h1 className="font-heading font-black text-xs sm:text-base tracking-[0.10em] sm:tracking-[0.14em] uppercase text-slate-900 dark:text-white leading-none truncate">
                  {currentUser.companyName && !isPlatformAdmin ? (
                    <>
                      <span>{currentUser.companyName}</span> <span className="text-slate-400 font-semibold text-[11px] sm:text-sm">CRM</span>
                    </>
                  ) : (
                    <>
                      JOHNSON <span className="text-cyan-500 dark:text-cyan-400">SOFTWARES</span> <span className="text-slate-400 font-semibold text-[11px] sm:text-sm">CRM</span>
                    </>
                  )}
                </h1>
                <span className="hidden md:inline-block text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-primary/10 text-primary font-mono shrink-0">
                  {currentUser.companyName && !isPlatformAdmin ? "Enterprise Workspace" : "Pipeline Intelligence"}
                </span>
              </div>
              <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate flex items-center gap-1.5">
                <strong className="text-slate-700 dark:text-slate-200">{currentUser.fullName}</strong>{" "}
                <span className="text-slate-400">({currentUser.role})</span>
              </p>
            </div>

            {/* Super Admin Workspace Switcher */}
            {isPlatformAdmin && (
              <div className="hidden lg:flex items-center gap-1.5 ml-2 bg-slate-100 dark:bg-slate-800/90 px-2.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 text-xs shadow-2xs">
                <Building2 className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
                <span className="text-slate-500 dark:text-slate-400 text-[11px] font-semibold">Workspace:</span>
                <select
                  value={workspaceScope}
                  onChange={(e) => setWorkspaceScope(e.target.value)}
                  className="bg-transparent text-xs font-bold text-slate-800 dark:text-slate-100 focus:outline-none cursor-pointer max-w-[240px] truncate"
                  title="Switch between your platform pipeline and client tenant consoles"
                >
                  <option value="all" className="bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100">
                    🌐 All Workspaces &amp; Leads
                  </option>
                  <option value="mine" className="bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100">
                    🏢 Johnson Softwares Direct Leads
                  </option>
                  {tenants.map((t) => (
                    <option key={t.id} value={String(t.id)} className="bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100">
                      👤 {t.companyName || t.fullName} ({t.username}) · {t.leadCount} leads
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Right Session Utilities */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Website Redirect Button */}
            <Link
              href="/"
              title="Return to Johnson Softwares Public Website"
              className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-semibold transition-all cursor-pointer shadow-xs"
            >
              <Globe className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
              <span className="hidden sm:inline">Website</span>
            </Link>

            {/* Trial Quota Usage & Plan Upgrade Badge */}
            {subscription && (
              <div className="flex items-center gap-1 sm:gap-2">
                <QuotaUsageBadge
                  subscription={subscription}
                  onOpenUpgrade={() => setIsUpgradeModalOpen(true)}
                />
                <button
                  onClick={() => setIsUpgradeModalOpen(true)}
                  className="px-2 sm:px-2.5 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1 sm:gap-1.5 transition-all bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-sm shadow-cyan-500/20 cursor-pointer"
                  title="Upgrade or Renew Plan Tier"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Upgrade</span>
                </button>
              </div>
            )}

            {/* PWA App Install Button (Tier 3 Desktop/Mobile Ready) */}
            <CrmPwaInstallButton />

            {/* Refresh Button */}
            <button
              onClick={fetchData}
              title="Refresh Pipeline"
              className="p-1.5 sm:p-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-colors cursor-pointer shadow-xs"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-primary" : ""}`} />
            </button>

            {/* Sign Out Button */}
            <button
              onClick={onLogout}
              title="Sign Out"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-600 dark:hover:text-red-400 text-slate-600 dark:text-slate-400 text-xs font-semibold transition-all cursor-pointer shadow-xs"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>

        {/* Row 2: Pipeline Navigation View Modes & Primary Actions */}
        <div className="max-w-[1700px] mx-auto flex items-center justify-between gap-3 pt-2.5 border-t border-slate-100 dark:border-slate-800/60 flex-wrap sm:flex-nowrap">
          {/* View Mode Toggle with horizontal scroll if screen is narrow */}
          <div className="flex items-center p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-x-auto max-w-full">
            <button
              onClick={() => setViewMode("kanban")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer ${viewMode === "kanban"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-sm"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-white"
                }`}
            >
              <Kanban className="w-3.5 h-3.5" />
              Kanban
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer ${viewMode === "table"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-sm"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-white"
                }`}
            >
              <TableIcon className="w-3.5 h-3.5" />
              Table
            </button>
            <button
              onClick={() => setViewMode("team")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer ${viewMode === "team"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-sm"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-white"
                }`}
            >
              <Users className="w-3.5 h-3.5 text-cyan-400" />
              Team &amp; Access
            </button>
            <button
              onClick={() => setViewMode("agreements")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer ${viewMode === "agreements"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-sm"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-white"
                }`}
            >
              <FileText className="w-3.5 h-3.5 text-amber-400" />
              Proposals &amp; SOW
            </button>
            {isPlatformAdmin && (
              <button
                onClick={() => setViewMode("ai-sales")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer ${viewMode === "ai-sales"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-800 dark:hover:text-white"
                  }`}
                title="Autonomous AI Sales Outreach (Johnson Softwares Internal Engine)"
              >
                <Bot className="w-3.5 h-3.5 text-blue-400" />
                AI Sales Rep
                {pendingOutreachCount > 0 && (
                  <span className="px-1.5 py-0.2 rounded-full bg-amber-500 text-white text-[10px] font-black animate-pulse">
                    {pendingOutreachCount}
                  </span>
                )}
              </button>
            )}
            {isPlatformAdmin && (
              <button
                onClick={() => setViewMode("trials")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer ${
                  viewMode === "trials"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-800 dark:hover:text-white"
                }`}
                title="Manage Client Trials, Extensions, and Paid Conversions"
              >
                <Clock className="w-3.5 h-3.5 text-blue-400" />
                Trial Monitor
              </button>
            )}
            <button
              onClick={() => setViewMode("reports")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer ${viewMode === "reports"
                  ? "bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm font-bold"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-white"
                }`}
            >
              <BarChart3 className="w-3.5 h-3.5 text-indigo-500" />
              Reports &amp; Analytics
            </button>
            <button
              onClick={() => setViewMode("compliance")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer ${viewMode === "compliance"
                  ? "bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-400 shadow-sm font-bold"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-white"
                }`}
            >
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              Compliance &amp; Invoices
            </button>
            {isPlatformAdmin && (
              <button
                onClick={() => setIsPaymentModalOpen(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 whitespace-nowrap cursor-pointer"
                title="View HDFC Bank, UPI QR Code & International PayPal Details"
              >
                <CreditCard className="w-3.5 h-3.5 text-emerald-500" />
                Payments &amp; Banking
              </button>
            )}
          </div>

          {/* Pipeline Action Buttons */}
          <div className="flex items-center gap-2.5 shrink-0">
            {/* Manual Add Lead for Live Demos & Prospecting */}
            <Button
              onClick={() => setIsAddLeadModalOpen(true)}
              variant="primary"
              size="sm"
              className="text-xs flex items-center gap-1.5 h-8.5 cursor-pointer bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-700 text-white font-bold shadow-sm shadow-primary/20 whitespace-nowrap"
              title="Add a new lead or inquiry to your CRM workspace"
            >
              <UserPlus className="w-3.5 h-3.5" />
              + Add Lead
            </Button>

            {/* Import CSV Button */}
            <Button
              onClick={() => setIsImportModalOpen(true)}
              variant="outline"
              size="sm"
              className="text-xs flex items-center gap-1.5 h-8.5 cursor-pointer whitespace-nowrap border-cyan-500/30 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 font-semibold"
              title="Upload leads in bulk from a CSV file"
            >
              <ArrowDownToLine className="w-3.5 h-3.5" />
              Import CSV
            </Button>

            {/* Workspace & Pipeline Settings Button */}
            <Button
              onClick={() => setIsWorkspaceSettingsOpen(true)}
              variant="outline"
              size="sm"
              className="text-xs flex items-center gap-1.5 h-8.5 cursor-pointer whitespace-nowrap border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-semibold"
              title="Customize business profile, field labels, and pipeline stages"
            >
              <Sliders className="w-3.5 h-3.5 text-primary" />
              Pipeline &amp; Fields
            </Button>

            {/* Export CSV Button */}
            <Button
              onClick={handleExportCsv}
              disabled={exporting}
              variant="outline"
              size="sm"
              className="text-xs flex items-center gap-1.5 h-8.5 cursor-pointer whitespace-nowrap"
            >
              {exporting ? (
                <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              ) : (
                <ArrowUpFromLine className="w-3.5 h-3.5" />
              )}
              {exporting ? "Exporting..." : "Export CSV"}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Dashboard Body */}
      <main className="flex-1 max-w-[1700px] w-full mx-auto px-4 sm:px-8 py-6 space-y-6">


        {/* KPI Summary Cards */}
        {viewMode !== "team" && viewMode !== "agreements" && viewMode !== "ai-sales" && viewMode !== "compliance" && stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
            {/* Total Leads */}
            <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="flex items-center justify-between text-slate-500 text-xs font-semibold mb-1">
                <span>Total Enquiries</span>
                <Users className="w-4 h-4 text-blue-500" />
              </div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">
                {stats.totalLeads}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Real marketing inquiries</p>
            </div>

            {/* Active Deals */}
            <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="flex items-center justify-between text-slate-500 text-xs font-semibold mb-1">
                <span>Active Pipeline</span>
                <Briefcase className="w-4 h-4 text-amber-500" />
              </div>
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {stats.activeOpportunities}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Open deals in review</p>
            </div>

            {/* Pipeline Value */}
            <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="flex items-center justify-between text-slate-500 text-xs font-semibold mb-1">
                <span>Pipeline Value</span>
                <DollarSign className="w-4 h-4 text-emerald-500" />
              </div>
              <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {(() => {
                  const parts: string[] = [];
                  if ((stats.pipelineValueAED || 0) > 0) parts.push(`AED ${stats.pipelineValueAED?.toLocaleString()}`);
                  if ((stats.pipelineValueUSD || 0) > 0) parts.push(`$${stats.pipelineValueUSD?.toLocaleString()}`);
                  if ((stats.pipelineValueINR || 0) > 0) parts.push(`₹${stats.pipelineValueINR?.toLocaleString("en-IN")}`);

                  if (parts.length === 0) return <span>₹0</span>;
                  if (parts.length === 1) return <span>{parts[0]}</span>;
                  return (
                    <div className="truncate text-base font-bold">
                      <span>{parts[0]}</span>
                      <span className="text-xs font-semibold text-slate-500 ml-1.5">
                        + {parts.slice(1).join(" • ")}
                      </span>
                    </div>
                  );
                })()}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                {(stats.pipelineValueINR || 0) > 0 || (stats.pipelineValueAED || 0) > 0
                  ? `~ $${stats.pipelineValue.toLocaleString()} combined est.`
                  : "Active estimated revenue"}
              </p>
            </div>

            {/* Won Revenue */}
            <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="flex items-center justify-between text-slate-500 text-xs font-semibold mb-1">
                <span>Closed Won</span>
                <CheckCircle className="w-4 h-4 text-primary" />
              </div>
              <div className="text-xl font-bold text-primary">
                {(() => {
                  const parts: string[] = [];
                  if ((stats.wonRevenueAED || 0) > 0) parts.push(`AED ${stats.wonRevenueAED?.toLocaleString()}`);
                  if ((stats.wonRevenueUSD || 0) > 0) parts.push(`$${stats.wonRevenueUSD?.toLocaleString()}`);
                  if ((stats.wonRevenueINR || 0) > 0) parts.push(`₹${stats.wonRevenueINR?.toLocaleString("en-IN")}`);

                  if (parts.length === 0) return <span>₹0</span>;
                  if (parts.length === 1) return <span>{parts[0]}</span>;
                  return (
                    <div className="truncate text-base font-bold">
                      <span>{parts[0]}</span>
                      <span className="text-xs font-semibold text-slate-500 ml-1.5">
                        + {parts.slice(1).join(" • ")}
                      </span>
                    </div>
                  );
                })()}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                {stats.wonDealsCount} contracts executed
              </p>
            </div>


            {/* Conversion Rate */}
            <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm col-span-2 sm:col-span-1">
              <div className="flex items-center justify-between text-slate-500 text-xs font-semibold mb-1">
                <span>Win Rate</span>
                <TrendingUp className="w-4 h-4 text-violet-500" />
              </div>
              <div className="text-2xl font-bold text-violet-600 dark:text-violet-400">
                {stats.conversionRate}%
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Inquiry-to-contract ratio</p>
            </div>
          </div>
        )}

        {/* Filter & Search Bar */}
        {viewMode !== "team" && viewMode !== "agreements" && viewMode !== "ai-sales" && viewMode !== "compliance" && (
          <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {/* Search Input */}
              <div className="lg:col-span-2 relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <Search className="w-4 h-4" />
                </span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search name, email, company, campaign..."
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              {/* Status Filter (Dynamic Stages) */}
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-primary/50"
                >
                  <option value="ALL">All Stages</option>
                  {workspaceConfig.pipelineStages.map((stage) => (
                    <option key={stage.id} value={stage.name}>
                      {stage.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Priority Filter */}
              <div>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-primary/50"
                >
                  <option value="ALL">All Priorities</option>
                  <option value="HOT">🔥 Hot Only</option>
                  <option value="WARM">🟡 Warm Only</option>
                  <option value="COLD">❄️ Cold Only</option>
                </select>
              </div>

              {/* Date Scope Filter */}
              <div>
                <select
                  value={dateScopeFilter}
                  onChange={(e) => setDateScopeFilter(e.target.value as any)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-primary/50"
                >
                  <option value="ALL">📅 All Time (History)</option>
                  <option value="TODAY">⚡ Today's Inquiries</option>
                  <option value="7DAYS">📆 Last 7 Days</option>
                  <option value="30DAYS">🗓️ Last 30 Days</option>
                  <option value="THIS_MONTH">📊 This Month</option>
                </select>
              </div>

              {/* Lead Mode (Real vs Test) */}
              <div>
                <select
                  value={leadModeFilter}
                  onChange={(e) => setLeadModeFilter(e.target.value as "REAL" | "TEST" | "ALL")}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-primary/50"
                >
                  <option value="ALL">All Modes (Real + Test)</option>
                  <option value="REAL">Real Inquiries Only</option>
                  <option value="TEST">Test Leads Only</option>
                </select>
              </div>

              {/* Assigned Rep Filter */}
              {currentUser.role !== "agent" && teamMembers.length > 0 && (
                <div>
                  <select
                    value={repFilter}
                    onChange={(e) => setRepFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="ALL">All Reps</option>
                    <option value="UNASSIGNED">Unassigned Only</option>
                    {teamMembers.map((m) => (
                      <option key={m.id} value={m.full_name}>
                        {m.full_name} ({m.role === "admin" ? "Admin" : m.role === "manager" ? "Manager" : "Agent"})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Views: Compliance vs Reports vs AI Sales vs Agreements vs Team vs Kanban vs Table */}
        {viewMode === "compliance" ? (
          <ComplianceTab />
        ) : viewMode === "reports" ? (
          <ReportManagementTab />
        ) : (viewMode === "ai-sales" && isPlatformAdmin) ? (
          <AiSalesAgentTab currentUser={currentUser} />
        ) : (viewMode === "trials" && isPlatformAdmin) ? (
          <TenantTrialsTab
            onSwitchWorkspace={(tenantId) => {
              setWorkspaceScope(String(tenantId));
              setViewMode("kanban");
            }}
          />
        ) : viewMode === "agreements" ? (
          <ClientAgreementGenerator currentUser={currentUser} />
        ) : viewMode === "team" ? (
          <TeamManagementTab currentUser={currentUser} />
        ) : viewMode === "kanban" ? (
          <>
            {leads.length === 0 && !loading && (
              <div className="mb-6 p-5 sm:p-6 rounded-2xl bg-gradient-to-r from-blue-950/40 via-slate-900 to-indigo-950/40 border border-blue-500/30 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 animate-in fade-in">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🏢</span>
                    <h3 className="font-bold text-sm sm:text-base text-white">
                      Welcome to Your {workspaceConfig.businessProfile === "real_estate" ? "Real Estate" : "CRM"} Pipeline!
                    </h3>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 uppercase tracking-wider">
                      7-Day Trial Active
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 max-w-2xl leading-relaxed">
                    Your pipeline is currently clear. Add your first live buyer inquiry to test sub-15s WhatsApp engagement and site visits, or pre-seed 3 realistic sample inquiries to test-drive features immediately.
                  </p>
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                  <button
                    onClick={handleSeedSampleLeads}
                    disabled={isSeedingLeads}
                    className="px-4 py-2 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/30 text-cyan-400 font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-sm disabled:opacity-50"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>{isSeedingLeads ? "Loading..." : "Load 3 Sample Inquiries"}</span>
                  </button>
                  <button
                    onClick={() => setIsAddLeadModalOpen(true)}
                    className="px-4 py-2 rounded-xl bg-primary hover:bg-primary/90 text-white font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-md shadow-primary/20"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>+ Add Live Lead</span>
                  </button>
                </div>
              </div>
            )}
            <KanbanBoard
              leads={leads}
              onSelectLead={(id) => setSelectedLeadId(id)}
              onStatusChange={handleStatusChange}
              onOpenChat={(lead) => setChatLead(lead)}
              pipelineStages={workspaceConfig.pipelineStages}
              onOpenSettings={() => setIsWorkspaceSettingsOpen(true)}
            />
          </>
        ) : (
          <div className="space-y-4">
            {/* Mobile Lead Cards (< md screen) */}
            <div className="block md:hidden space-y-3">
              {leads.length === 0 ? (
                <div className="p-8 text-center text-slate-400 italic bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
                  No leads matching current filters.
                </div>
              ) : (
                leads.map((lead) => (
                  <div
                    key={lead.id}
                    onClick={() => setSelectedLeadId(lead.id)}
                    className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm active:scale-[0.99] transition-all cursor-pointer space-y-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-slate-400">
                            #{lead.id}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                              lead.priority === "HOT"
                                ? "bg-rose-500/10 text-rose-500 border-rose-500/20"
                                : lead.priority === "WARM"
                                ? "bg-amber-500/10 text-amber-500 border-amber-500/20"
                                : "bg-blue-500/10 text-blue-500 border-blue-500/20"
                            }`}
                          >
                            {lead.priority}
                          </span>
                        </div>
                        <h4 className="font-bold text-slate-900 dark:text-white text-base mt-1 truncate">
                          {lead.name}
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5 truncate">
                          <Building2 className="w-3.5 h-3.5 shrink-0" />
                          {lead.company_name || lead.email}
                        </p>
                      </div>

                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                          {formatCurrencyValue(lead.estimated_deal_value, lead.currency)}
                        </div>
                        <div className="text-[10px] text-slate-400 uppercase font-medium">
                          {getCurrencyBadge(lead.currency)}
                        </div>
                      </div>
                    </div>

                    {/* Pipeline Stage Quick Selector */}
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
                      <div className="text-[11px] font-medium text-slate-500">Stage:</div>
                      <select
                        value={lead.status}
                        onChange={(e) => handleStatusChange(lead.id, e.target.value as any)}
                        className="flex-1 max-w-[180px] px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200"
                      >
                        {workspaceConfig.pipelineStages.map((stage) => (
                          <option key={stage.id} value={stage.name}>
                            {stage.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Fast Mobile Action Buttons: Call, WhatsApp, View Details */}
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      {lead.phone && (
                        <>
                          <a
                            href={`tel:${lead.phone}`}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 active:bg-emerald-500/20 transition-colors"
                            title="Call customer directly"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            Call
                          </a>
                          <button
                            onClick={() => setChatLead(lead)}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white shadow-xs active:bg-emerald-700 transition-colors"
                            title="Open WhatsApp Chat"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                            WhatsApp
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setSelectedLeadId(lead.id)}
                        className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Details
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Desktop Table View (>= md screen) */}
            <div className="hidden md:block rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-950/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 uppercase font-semibold">
                    <tr>
                      <th className="px-4 py-3.5">ID</th>
                      <th className="px-4 py-3.5">{getFieldLabel("name", "Client & Company")}</th>
                      <th className="px-4 py-3.5">{getFieldLabel("service_type", "Product / Service")}</th>
                      {isFieldVisible("quantity") && (
                        <th className="px-4 py-3.5">{getFieldLabel("quantity", "Quantity / Volume")}</th>
                      )}
                      <th className="px-4 py-3.5">{getFieldLabel("estimated_deal_value", "Deal Value")}</th>
                      <th className="px-4 py-3.5">Pipeline Stage</th>
                      <th className="px-4 py-3.5">Priority</th>
                      <th className="px-4 py-3.5">Assigned Rep</th>
                      <th className="px-4 py-3.5">Captured</th>
                      <th className="px-4 py-3.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                    {leads.length === 0 ? (
                      <tr>
                        <td colSpan={isFieldVisible("quantity") ? 10 : 9} className="px-6 py-12 text-center text-slate-400 italic">
                          No inquiries matching current filters.
                        </td>
                      </tr>
                    ) : (
                      leads.map((lead) => (
                        <tr
                          key={lead.id}
                          className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                          onClick={() => setSelectedLeadId(lead.id)}
                        >
                          <td className="px-4 py-3 font-mono font-bold text-slate-500">
                            #{lead.id}
                          </td>

                          <td className="px-4 py-3">
                            <div className="font-bold text-slate-900 dark:text-white line-clamp-1">
                              {lead.name}
                            </div>
                            <div className="text-[11px] text-slate-400 line-clamp-1 flex items-center gap-1 mt-0.5">
                              <Building2 className="w-3 h-3" />
                              {lead.company_name || lead.email}
                            </div>
                            {lead.is_duplicate === 1 && (
                              <span className="inline-block mt-1 text-[9px] font-bold px-1.5 py-0.2 rounded bg-rose-500/10 text-rose-500 border border-rose-500/20">
                                Duplicate of #{lead.duplicate_of_id}
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300 font-medium">
                            <span className="line-clamp-1 font-semibold text-slate-800 dark:text-slate-200">
                              {lead.service_type || "General Inquiry"}
                            </span>
                          </td>

                          {isFieldVisible("quantity") && (
                            <td className="px-4 py-3 text-slate-600 dark:text-slate-300 font-medium">
                              {lead.quantity ? (
                                <span className="font-bold text-primary">
                                  {lead.quantity.toLocaleString()} {lead.unit || ""}
                                </span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                          )}

                          <td className="px-4 py-3 font-semibold text-slate-900 dark:text-white">
                            {formatCurrencyValue(lead.estimated_deal_value, lead.currency)}
                            <span className="ml-1 text-[10px] text-slate-400 font-normal">
                              {getCurrencyBadge(lead.currency)}
                            </span>
                          </td>

                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={lead.status}
                              onChange={(e) => handleStatusChange(lead.id, e.target.value as any)}
                              className="px-2 py-1 rounded-lg text-xs font-semibold border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer shadow-xs"
                            >
                              {workspaceConfig.pipelineStages.map((stage) => (
                                <option key={stage.id} value={stage.name}>
                                  {stage.name}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={lead.priority}
                              onChange={(e) => handlePriorityChange(lead.id, e.target.value as any)}
                              className={`px-2 py-0.5 rounded-full text-[11px] font-bold border cursor-pointer focus:outline-none ${
                                lead.priority === "HOT"
                                  ? "bg-rose-500/10 text-rose-500 border-rose-500/20"
                                  : lead.priority === "WARM"
                                  ? "bg-amber-500/10 text-amber-500 border-amber-500/20"
                                  : "bg-blue-500/10 text-blue-500 border-blue-500/20"
                              }`}
                            >
                              <option value="HOT" className="text-slate-900 dark:text-white bg-white dark:bg-slate-900">HOT</option>
                              <option value="WARM" className="text-slate-900 dark:text-white bg-white dark:bg-slate-900">WARM</option>
                              <option value="COLD" className="text-slate-900 dark:text-white bg-white dark:bg-slate-900">COLD</option>
                            </select>
                          </td>

                          <td className="px-4 py-3 text-slate-500 text-[11px]">
                            {lead.assigned_rep || "Unassigned"}
                          </td>

                          <td className="px-4 py-3 text-[11px] font-mono text-primary truncate max-w-[120px]">
                            {lead.utm_source || "direct"}
                          </td>

                          <td className="px-4 py-3 text-[11px] text-slate-400 whitespace-nowrap">
                            {new Date(lead.created_at).toLocaleDateString()}
                          </td>

                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {lead.phone && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setChatLead(lead);
                                  }}
                                  title="Open Live WhatsApp Chat"
                                  className="h-7 w-7 p-0 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10"
                                >
                                  <MessageSquare className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedLeadId(lead.id);
                                }}
                                className="h-7 px-2.5 text-xs text-primary hover:bg-primary/10"
                              >
                                <Eye className="w-3.5 h-3.5 mr-1" />
                                Details
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Floating Action Button (FAB) for Instant Lead Capture */}
        <div className="md:hidden fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setIsAddLeadModalOpen(true)}
            className="w-14 h-14 rounded-full bg-gradient-to-r from-primary to-blue-600 text-white shadow-xl shadow-primary/30 flex items-center justify-center cursor-pointer active:scale-95 transition-transform border border-white/20"
            title="Add Lead (Mobile Quick)"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
      </main>

      {/* Lead Detail & Chronological Audit Drawer */}
      {selectedLeadId && (
        <LeadDetailModal
          leadId={selectedLeadId}
          onClose={() => setSelectedLeadId(null)}
          onLeadUpdated={fetchData}
          onOpenChat={(lead) => {
            setSelectedLeadId(null);
            setChatLead(lead);
          }}
          pipelineStages={workspaceConfig?.pipelineStages}
          fieldLabels={
            workspaceConfig
              ? Object.fromEntries(workspaceConfig.fields.map((f) => [f.key, f.label]))
              : undefined
          }
          workspaceConfig={workspaceConfig}
          isPlatformAdmin={isPlatformAdmin}
          currentUser={currentUser}
          teamMembers={teamMembers}
        />
      )}

      {/* In-App WhatsApp Live Chat Drawer */}
      <WhatsAppChatDrawer
        lead={chatLead}
        isOpen={!!chatLead}
        onClose={() => setChatLead(null)}
        onMessageSent={fetchData}
      />

      {/* Stage Approval & Review Modal for Proposal, Negotiation & Won */}
      {stageApprovalData && (
        <StageApprovalModal
          lead={stageApprovalData.lead}
          targetStage={stageApprovalData.targetStage}
          onClose={() => setStageApprovalData(null)}
          onConfirm={({ status, estimated_deal_value, currency, options }) => {
            executeStatusUpdate(stageApprovalData.lead.id, status, estimated_deal_value, currency, options);
            setStageApprovalData(null);
          }}
        />
      )}

      {/* Payment Management & Banking Remittance Modal */}
      <PaymentManagementModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />

      {/* Manual Add Lead Modal for Live Demos & Prospecting */}
      {isAddLeadModalOpen && (
        <AddLeadModal
          isOpen={isAddLeadModalOpen}
          onClose={() => setIsAddLeadModalOpen(false)}
          onLeadCreated={(newLead, welcomeSent) => {
            fetchData();
            if (welcomeSent) {
              setChatLead(newLead);
            }
          }}
          workspaceConfig={workspaceConfig}
          currentUser={currentUser}
          teamMembers={teamMembers}
          targetTenantId={isPlatformAdmin && workspaceScope !== "mine" && workspaceScope !== "all" ? Number(workspaceScope) : undefined}
        />
      )}

      {/* Workspace Configuration & Customization Modal */}
      {isWorkspaceSettingsOpen && (
        <WorkspaceSettingsModal
          isOpen={isWorkspaceSettingsOpen}
          onClose={() => setIsWorkspaceSettingsOpen(false)}
          config={workspaceConfig}
          isPlatformAdmin={isPlatformAdmin}
          onSave={(newCfg) => {
            setWorkspaceConfig(newCfg);
            fetchData();
          }}
        />
      )}

      {/* CSV Bulk Leads Import Modal */}
      <ImportLeadsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => {
          fetchData();
        }}
        workspaceConfig={workspaceConfig}
        targetTenantId={isPlatformAdmin && workspaceScope !== "mine" && workspaceScope !== "all" ? Number(workspaceScope) : undefined}
        targetTenantName={isPlatformAdmin && workspaceScope !== "mine" && workspaceScope !== "all" ? tenants.find(t => String(t.id) === workspaceScope)?.companyName || tenants.find(t => String(t.id) === workspaceScope)?.fullName : undefined}
      />

      {/* 7-Day Free Trial Renewal & Tier Upgrade Modal */}
      <UpgradePlanModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        currentTrialDays={subscription?.daysRemaining ?? 14}
        initialTier={subscription?.planTier === "tier2" ? "tier2" : subscription?.planTier === "tier3" ? "tier3" : "tier1"}
        onUpgradeSuccess={() => {
          fetchData();
          if (onSubscriptionUpdate) onSubscriptionUpdate();
        }}
      />



      {/* Real-Time Workflow Automation Notification Toast */}
      {automationNotification && (
        <div className="fixed bottom-6 right-6 z-50 max-w-md p-4 rounded-2xl bg-slate-900/95 dark:bg-white/95 text-white dark:text-slate-900 border border-slate-700/80 dark:border-slate-200 shadow-2xl backdrop-blur-xl flex items-start gap-3 transition-all">
          <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center text-white shrink-0 font-bold text-sm shadow-md">
            ⚡
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-wider text-primary">
                Stage Automation Executed
              </h4>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                {automationNotification.stage}
              </span>
            </div>
            <p className="text-xs mt-1 text-slate-200 dark:text-slate-700 leading-relaxed font-medium">
              {automationNotification.message}
            </p>
          </div>
          <button
            onClick={() => setAutomationNotification(null)}
            className="text-xs opacity-60 hover:opacity-100 p-1 -mr-1 -mt-1"
          >
            ✕
          </button>
        </div>
      )}
    </div>

  );
}
